import { api } from '@/lib/axios';
import { type PdfMode, renderPdf } from '@/lib/pdf';
import type { Page } from '@/services/chantiers';

export type TypeContrat = 'CDI' | 'CDD' | 'ANAPEC' | 'INTERIM';
export type BulletinStatut = 'BROUILLON' | 'VALIDE' | 'PAYE';
export type EmployeStatut = 'ACTIF' | 'SUSPENDU' | 'CONGE' | 'SORTI';
export type ModePaiement = 'ESPECES' | 'CHEQUE' | 'VIREMENT' | 'CARTE' | 'PRELEVEMENT';
export type AvanceStatut = 'DEMANDEE' | 'ACCORDEE' | 'REMBOURSEE' | 'REFUSEE';
export type VisiteType = 'EMBAUCHE' | 'PERIODIQUE' | 'REPRISE' | 'SPONTANEE';
export type VisiteResultat = 'APTE' | 'APTE_AVEC_RESERVE' | 'INAPTE' | 'EN_ATTENTE';

export const TYPES_CONTRAT: TypeContrat[] = ['CDI', 'CDD', 'ANAPEC', 'INTERIM'];
export const EMPLOYE_STATUTS: EmployeStatut[] = ['ACTIF', 'SUSPENDU', 'CONGE', 'SORTI'];
export const MODES_PAIEMENT_RH: ModePaiement[] = ['VIREMENT', 'ESPECES', 'CHEQUE', 'CARTE', 'PRELEVEMENT'];
export const AVANCE_STATUTS: AvanceStatut[] = ['DEMANDEE', 'ACCORDEE', 'REMBOURSEE', 'REFUSEE'];
export const VISITE_TYPES: VisiteType[] = ['EMBAUCHE', 'PERIODIQUE', 'REPRISE', 'SPONTANEE'];
export const VISITE_RESULTATS: VisiteResultat[] = ['APTE', 'APTE_AVEC_RESERVE', 'INAPTE', 'EN_ATTENTE'];

export interface EmployeDTO {
  id: string;
  matricule: string;
  nom: string;
  prenom: string;
  date_naissance: string | null;
  lieu_naissance: string | null;
  nin: string | null;
  cnas_num: string | null;
  poste: string | null;
  departement: string | null;
  categorie: string | null;
  grade: string | null;
  type_contrat: TypeContrat;
  statut: EmployeStatut;
  salaire_base: number;
  mode_paiement: ModePaiement;
  date_embauche: string | null;
  date_fin_contrat: string | null;
  telephone: string | null;
  email: string | null;
  adresse: string | null;
  ville: string | null;
  wilaya_id: number | null;
  rib_banque: string | null;
  rib_agence: string | null;
  rib_compte: string | null;
  rib_cle: string | null;
  situation_familiale: string | null;
  nombre_enfants: number;
  is_active: boolean;
}

export interface EmployeInput {
  matricule?: string | null;
  nom: string;
  prenom: string;
  date_naissance?: string | null;
  lieu_naissance?: string | null;
  nin?: string | null;
  cnas_num?: string | null;
  poste?: string | null;
  departement?: string | null;
  categorie?: string | null;
  grade?: string | null;
  type_contrat: TypeContrat;
  statut?: EmployeStatut;
  salaire_base: number;
  mode_paiement?: ModePaiement;
  date_embauche?: string | null;
  date_fin_contrat?: string | null;
  telephone?: string | null;
  email?: string | null;
  adresse?: string | null;
  ville?: string | null;
  wilaya_id?: number | null;
  rib_banque?: string | null;
  rib_agence?: string | null;
  rib_compte?: string | null;
  rib_cle?: string | null;
  situation_familiale?: string | null;
  nombre_enfants?: number;
  is_archived?: boolean;
}

export interface ContratEmployeDTO {
  id: string;
  employe_id: string;
  reference: string | null;
  type_contrat: TypeContrat;
  poste: string | null;
  salaire_base: number;
  date_debut: string | null;
  date_fin: string | null;
  actif: boolean;
  document_url: string | null;
  notes: string | null;
}

export interface AvanceSalaireDTO {
  id: string;
  employe_id: string;
  date_avance: string;
  montant: number;
  motif: string | null;
  mode_paiement: ModePaiement;
  statut: AvanceStatut;
  date_remboursement: string | null;
  notes: string | null;
}

export interface VisiteMedicaleDTO {
  id: string;
  employe_id: string;
  date_visite: string;
  type: VisiteType;
  resultat: VisiteResultat;
  medecin: string | null;
  date_prochaine: string | null;
  observations: string | null;
  document_url: string | null;
}

export interface BulletinDTO {
  id: string;
  employe_id: string;
  mois: number;
  annee: number;
  salaire_base: number;
  jours_travailles: number;
  jours_ouvrables: number;
  salaire_poste: number;
  retenue_absence: number;
  iep_montant: number;
  prime_rendement: number;
  prime_chantier: number;
  heures_sup_total: number;
  heures_sup_montant: number;
  indemnite_transport: number;
  indemnite_panier: number;
  indemnite_zone: number;
  indemnite_nuisance: number;
  allocations_familiales: number;
  autres_primes: number;
  brut_imposable: number;
  cnas_salarie_montant: number;
  irg_montant: number;
  autres_retenues: number;
  avances: number;
  net_a_payer: number;
  cnas_patronal_montant: number;
  cout_total_employeur: number;
  statut: BulletinStatut;
}

export interface CalculPaieInput {
  employe_id: string;
  mois: number;
  annee: number;
  /** Salaire de base : surcharge le contrat/fiche pour ce calcul (Axe 1). */
  salaire_base?: number;
  jours_ouvrables?: number;
  /** Jours travaillés : surcharge la valeur du pointage (Axe 2). */
  jours_travailles?: number;
  /** Prime d'ancienneté (IEP) : surcharge le calcul auto (Axe 4). */
  iep_montant?: number;
  heures_sup_75?: number;
  heures_sup_100?: number;
  prime_rendement?: number;
  prime_chantier?: number;
  autres_primes?: number;
  indemnite_transport?: number;
  indemnite_panier?: number;
  indemnite_zone?: number;
  indemnite_nuisance?: number;
  allocations_familiales?: number;
  autres_retenues?: number;
  conjoint_a_charge?: boolean;
  handicape_retraite?: boolean;
}

/** Avance du mois (aperçu paie). */
export interface AvanceApercu {
  id: string;
  date_avance: string;
  montant: number;
  motif: string | null;
}

/** Valeurs pré-remplies du formulaire de paie pour (employé, mois). */
export interface ApercuPaie {
  salaire_base: number;
  salaire_source: 'contrat' | 'fiche' | 'aucun';
  jours_ouvrables: number;
  jours_travailles: number;
  pointage_source: 'cloture' | 'non_cloture' | 'manuel';
  pointage_cloture: boolean;
  anciennete_annees: number;
  iep_taux_pct: number;
  iep_montant: number;
  date_embauche_presente: boolean;
  heures_sup_25: number;
  heures_sup_50: number;
  avances_total: number;
  avances: AvanceApercu[];
}

export async function getApercuPaie(employe_id: string, mois: number, annee: number): Promise<ApercuPaie> {
  const { data } = await api.get<ApercuPaie>('/paie/apercu', { params: { employe_id, mois, annee } });
  return data;
}

export async function listEmployes(params?: { q?: string; actifs_seulement?: boolean; archives?: boolean }): Promise<Page<EmployeDTO>> {
  const { data } = await api.get<Page<EmployeDTO>>('/employes', { params });
  return data;
}

export async function createEmploye(payload: EmployeInput): Promise<EmployeDTO> {
  const { data } = await api.post<EmployeDTO>('/employes', payload);
  return data;
}

export async function updateEmploye(id: string, payload: Partial<EmployeInput>): Promise<EmployeDTO> {
  const { data } = await api.put<EmployeDTO>(`/employes/${id}`, payload);
  return data;
}

export async function deleteEmploye(id: string): Promise<void> {
  await api.delete(`/employes/${id}`);
}

/* -------------------------------- Contrats -------------------------------- */
export async function listContrats(employe_id?: string): Promise<ContratEmployeDTO[]> {
  const { data } = await api.get<ContratEmployeDTO[]>('/contrats', { params: { employe_id } });
  return data;
}
export async function createContrat(payload: Omit<ContratEmployeDTO, 'id'>): Promise<ContratEmployeDTO> {
  const { data } = await api.post<ContratEmployeDTO>('/contrats', payload);
  return data;
}
export async function updateContrat(id: string, payload: Partial<ContratEmployeDTO>): Promise<ContratEmployeDTO> {
  const { data } = await api.put<ContratEmployeDTO>(`/contrats/${id}`, payload);
  return data;
}
export async function deleteContrat(id: string): Promise<void> {
  await api.delete(`/contrats/${id}`);
}

/* ----------------------------- Avances salaire ---------------------------- */
export async function listAvances(employe_id?: string): Promise<AvanceSalaireDTO[]> {
  const { data } = await api.get<AvanceSalaireDTO[]>('/avances', { params: { employe_id } });
  return data;
}
export async function createAvance(payload: Omit<AvanceSalaireDTO, 'id'>): Promise<AvanceSalaireDTO> {
  const { data } = await api.post<AvanceSalaireDTO>('/avances', payload);
  return data;
}
export async function updateAvance(id: string, payload: Partial<AvanceSalaireDTO>): Promise<AvanceSalaireDTO> {
  const { data } = await api.put<AvanceSalaireDTO>(`/avances/${id}`, payload);
  return data;
}
export async function deleteAvance(id: string): Promise<void> {
  await api.delete(`/avances/${id}`);
}

/* ---------------------------- Visites médicales --------------------------- */
export async function listVisites(employe_id?: string): Promise<VisiteMedicaleDTO[]> {
  const { data } = await api.get<VisiteMedicaleDTO[]>('/visites-medicales', { params: { employe_id } });
  return data;
}
export async function createVisite(payload: Omit<VisiteMedicaleDTO, 'id'>): Promise<VisiteMedicaleDTO> {
  const { data } = await api.post<VisiteMedicaleDTO>('/visites-medicales', payload);
  return data;
}
export async function updateVisite(id: string, payload: Partial<VisiteMedicaleDTO>): Promise<VisiteMedicaleDTO> {
  const { data } = await api.put<VisiteMedicaleDTO>(`/visites-medicales/${id}`, payload);
  return data;
}
export async function deleteVisite(id: string): Promise<void> {
  await api.delete(`/visites-medicales/${id}`);
}

export async function calculerPaie(payload: CalculPaieInput): Promise<BulletinDTO> {
  const { data } = await api.post<BulletinDTO>('/paie/calculer', payload);
  return data;
}

export async function listBulletins(params?: { annee?: number; mois?: number }): Promise<Page<BulletinDTO>> {
  const { data } = await api.get<Page<BulletinDTO>>('/paie/bulletins', { params });
  return data;
}

/* -------------------------------- Pointages ------------------------------- */
export type PointageType =
  | 'PRESENT' | 'ABSENT_JUSTIFIE' | 'ABSENT_NON_JUSTIFIE' | 'CONGE' | 'MALADIE' | 'MISSION'
  | 'PERMISSION' | 'DEPLACEMENT' | 'FERIE' | 'DEMI_MATIN' | 'DEMI_APREM' | 'HORAIRE_LIBRE';

export interface PointageDTO {
  id: string;
  employe_id: string;
  chantier_id: string | null;
  date: string;
  type: PointageType;
  heures_normales: number;
  heures_sup_25: number;
  heures_sup_50: number;
  indemnite_panier: number;
  indemnite_transport: number;
}

export interface PointageJourInput {
  date: string;
  type: PointageType;
  heures_normales: number;
  heures_sup_25?: number;
  indemnite_panier?: number;
  indemnite_transport?: number;
}

export async function listPointages(employe_id: string, mois: number, annee: number): Promise<PointageDTO[]> {
  const { data } = await api.get<PointageDTO[]>('/pointages', { params: { employe_id, mois, annee } });
  return data;
}

export async function savePointages(payload: {
  employe_id: string;
  mois: number;
  annee: number;
  chantier_id?: string | null;
  jours: PointageJourInput[];
}): Promise<PointageDTO[]> {
  const { data } = await api.post<PointageDTO[]>('/pointages/bulk', payload);
  return data;
}

/* ----------------------- Pointage en tableau (mensuel) -------------------- */
export interface PointageCellule {
  type: PointageType;
  heures_normales: number;
  heures_sup_25: number;
  heures_sup_50: number;
  heure_arrivee: string | null;
  heure_depart: string | null;
  commentaire: string | null;
  chantier_id: string | null;
  verrouille: boolean; // Axe 7 — cellule clôturée (lecture seule sauf ADMIN)
}

export interface PointageLigneEmploye {
  employe_id: string;
  matricule: string;
  nom: string;
  prenom: string;
  poste: string | null;
  /** Clé = numéro du jour (1..31). */
  jours: Record<string, PointageCellule>;
  jours_travailles: number;
  jours_absents: number;
  jours_conge: number;
  heures_sup: number;
  total_heures: number;
}

export interface PointageMensuel {
  mois: number;
  annee: number;
  nb_jours: number;
  lignes: PointageLigneEmploye[];
}

/** Feuille de présence : matrice de tous les employés × jours du mois. */
export async function getPointageMensuel(mois: number, annee: number): Promise<PointageMensuel> {
  const { data } = await api.get<PointageMensuel>('/pointages/mensuel', { params: { mois, annee } });
  return data;
}

/** Saisie rapide d'un jour pour un employé (crée ou met à jour la case). */
export async function upsertPointageJour(payload: {
  employe_id: string;
  date: string;
  type: PointageType;
  heures_normales?: number | null;
  heure_arrivee?: string | null;
  heure_depart?: string | null;
  commentaire?: string | null;
  heures_sup_25?: number;
  heures_sup_50?: number;
  chantier_id?: string | null;
}): Promise<PointageDTO> {
  const { data } = await api.post<PointageDTO>('/pointages/jour', payload);
  return data;
}

/** Efface le pointage d'un employé pour un jour (remet la case à vide). */
export async function deletePointageJour(employe_id: string, jour: string): Promise<void> {
  await api.delete('/pointages/jour', { params: { employe_id, jour } });
}

/** Applique un type (ex. présent) à tous les employés actifs pour un jour. */
export async function remplirJour(jour: string, type: PointageType = 'PRESENT', employe_ids?: string[]): Promise<{ detail: string }> {
  const { data } = await api.post(`/pointages/jour/${jour}/remplir`, { type, employe_ids: employe_ids ?? null });
  return data;
}

/** Applique un type à tous les employés pour tout le mois (hors vendredi par défaut). */
export async function remplirMois(annee: number, mois: number, type: PointageType = 'PRESENT', horsRepos = true, employe_ids?: string[]): Promise<{ detail: string }> {
  const { data } = await api.post(`/pointages/mois/${annee}/${mois}/remplir`, { type, employe_ids: employe_ids ?? null }, { params: { hors_repos: horsRepos } });
  return data;
}

/** Ouvre ou télécharge le PDF d'un bulletin (avec jeton JWT). */
export async function openBulletinPdf(bulletinId: string, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf(`/paie/bulletins/${bulletinId}/pdf`, undefined, mode);
}

/** Ouvre ou télécharge la fiche individuelle de l'employé (PDF). */
export async function openFicheEmployePdf(employeId: string, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf(`/employes/${employeId}/fiche-pdf`, undefined, mode);
}

/** Ouvre ou télécharge le bordereau CNAS mensuel (PDF). */
export async function openBordereauCnas(mois: number, annee: number, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf('/paie/bordereau-cnas', { mois, annee }, mode);
}

/** Ouvre la feuille de présence imprimable (mensuelle = paysage, hebdo = portrait). */
export async function openFeuillePointage(vue: 'jour' | 'semaine' | 'mois', dateRef: string, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf('/pointages/feuille/pdf', { vue, date_ref: dateRef }, mode);
}

/* ------------------- Documents RH (attestation / certificat de travail) ------------------- */
export type TypeDocumentRH = 'ATTESTATION_TRAVAIL' | 'CERTIFICAT_TRAVAIL';

export interface ModeleDocumentRH {
  id: string;
  type: TypeDocumentRH;
  titre: string;
  corps: string;
  signataire: string;
  lieu: string | null;
}
export interface DocumentApercu {
  type: TypeDocumentRH;
  titre: string;
  corps: string;
  signataire: string;
  lieu: string | null;
  eligible: boolean;
  raison: string | null;
}
export interface DocumentRHEmis {
  id: string;
  type: TypeDocumentRH;
  numero: string;
  employe_id: string;
  date_emission: string;
  titre: string;
  signataire: string;
  lieu: string | null;
  emis_par_nom: string | null;
}

export async function getModelesDocuments(): Promise<ModeleDocumentRH[]> {
  const { data } = await api.get<ModeleDocumentRH[]>('/documents-rh/modeles');
  return data;
}
export async function updateModeleDocument(type: TypeDocumentRH, payload: Partial<Pick<ModeleDocumentRH, 'titre' | 'corps' | 'signataire' | 'lieu'>>): Promise<ModeleDocumentRH> {
  const { data } = await api.put<ModeleDocumentRH>(`/documents-rh/modeles/${type}`, payload);
  return data;
}
export async function getApercuDocument(employeId: string, type: TypeDocumentRH): Promise<DocumentApercu> {
  const { data } = await api.get<DocumentApercu>(`/employes/${employeId}/documents-rh/${type}/apercu`);
  return data;
}
export async function emettreDocument(employeId: string, payload: { type: TypeDocumentRH; titre: string; corps: string; signataire: string; lieu: string | null }): Promise<DocumentRHEmis> {
  const { data } = await api.post<DocumentRHEmis>(`/employes/${employeId}/documents-rh`, payload);
  return data;
}
export async function listDocumentsEmis(employeId: string): Promise<DocumentRHEmis[]> {
  const { data } = await api.get<DocumentRHEmis[]>(`/employes/${employeId}/documents-rh`);
  return data;
}
/** Ouvre ou télécharge le PDF d'un document RH émis (depuis sa copie figée). */
export async function openDocumentRhPdf(docId: string, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf(`/documents-rh/${docId}/pdf`, undefined, mode);
}

/* Axe 7 — clôture / déclôture d'une période (ADMIN ; vérifié côté serveur). */
export async function cloturerPointages(niveau: 'jour' | 'semaine' | 'mois', dateRef: string): Promise<{ detail: string }> {
  const { data } = await api.post<{ detail: string }>('/pointages/cloturer', null, { params: { niveau, date_ref: dateRef } });
  return data;
}
export async function decloturerPointages(niveau: 'jour' | 'semaine' | 'mois', dateRef: string): Promise<{ detail: string }> {
  const { data } = await api.post<{ detail: string }>('/pointages/decloturer', null, { params: { niveau, date_ref: dateRef } });
  return data;
}
